// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\rapports\SupervisorReportsPage.js

import React, { useState, useEffect } from 'react';
import { 
  Table, Card, Tag, Typography, Space, Button, 
  Input, Row, Col, Modal, message 
} from 'antd';
import { 
  SafetyCertificateOutlined, CrownOutlined, UserOutlined, 
  MedicineBoxOutlined, SearchOutlined, ReloadOutlined, 
  EyeOutlined, DownloadOutlined, StarFilled, FileTextOutlined 
} from '@ant-design/icons';
import axiosInstance from '../../services/axiosServicesVisit';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

const SupervisorReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('accessToken') || 
                    localStorage.getItem('token_auth');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axiosInstance.get('/v1/supervisors/all-control-reports', { headers: authHeaders });
      setReports(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de la récupération des rapports superviseurs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  // Filtre de recherche locale
  const filteredReports = reports.filter(r => {
    const q = searchText.toLowerCase();
    return (
      (r.supervisorName && r.supervisorName.toLowerCase().includes(q)) ||
      (r.delegateName && r.delegateName.toLowerCase().includes(q)) ||
      (r.doctorName && r.doctorName.toLowerCase().includes(q)) ||
      (r.region && r.region.toLowerCase().includes(q))
    );
  });

  // Export CSV
  const handleExportCSV = () => {
    if (filteredReports.length === 0) return;
    const headers = ["Date", "Superviseur", "Delegue(s) Controle(s)", "Medecin", "Specialite", "Region", "Type Visite", "Observations"];
    const rows = filteredReports.map(r => [
      dayjs(r.date).format('DD/MM/YYYY HH:mm'),
      `"${r.supervisorName}"`,
      `"${r.delegateName}"`,
      `"${r.doctorName}"`,
      `"${r.specialty}"`,
      `"${r.region}"`,
      `"${r.controlType}"`,
      `"${(r.comment || '').replace(/"/g, '""')}"`
    ]);
    const csvContent = [headers.join(";"), ...rows.map(e => e.join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Rapports_Superviseurs_${dayjs().format('YYYY-MM-DD')}.csv`;
    link.click();
  };

  // Modal d'observation complète
  const handleOpenCommentModal = (record) => {
    Modal.info({
      title: (
        <Space>
          <FileTextOutlined style={{ color: '#1890ff' }} />
          <span>Observations du Superviseur</span>
        </Space>
      ),
      width: 600,
      content: (
        <div style={{ marginTop: 15 }}>
          <p style={{ marginBottom: 6 }}>
            <strong>🏥 Médecin :</strong> {record.doctorName} ({record.specialty || 'N/A'})
          </p>
          <p style={{ marginBottom: 6 }}>
            <strong>👥 Délégué(s) accompagné(s) :</strong> {record.delegateName}
          </p>
          <p style={{ marginBottom: 14 }}>
            <strong>👑 Superviseur :</strong> {record.supervisorName} | <strong>📅 Date :</strong> {dayjs(record.date).format('DD/MM/YYYY HH:mm')}
          </p>
          <div style={{
            background: '#f9f9f9',
            border: '1px solid #e8e8e8',
            padding: '14px 18px',
            borderRadius: '8px',
            whiteSpace: 'pre-wrap',
            maxHeight: '380px',
            overflowY: 'auto',
            fontSize: '13px',
            lineHeight: '1.6',
            color: '#333'
          }}>
            {record.comment || record.fullFeedback || "Aucune observation."}
          </div>
        </div>
      ),
      okText: 'Fermer'
    });
  };

  const columns = [
    {
      title: 'Date Visite',
      dataIndex: 'date',
      key: 'date',
      width: 140,
      render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm')
    },
    {
      title: 'Superviseur (Visiteur)',
      dataIndex: 'supervisorName',
      key: 'supervisorName',
      width: 160,
      render: (name) => (
        <Tag color="purple" style={{ fontWeight: 'bold', fontSize: '12px', padding: '3px 8px' }}>
          <CrownOutlined style={{ marginRight: 5 }} />
          {name}
        </Tag>
      )
    },
    {
      title: 'Délégué(s) Contrôlé(s)',
      dataIndex: 'delegateName',
      key: 'delegateName',
      width: 180,
      render: (name) => {
        if (!name) return <Text type="secondary">-</Text>;
        const delegatesList = String(name).split(',').map(n => n.trim()).filter(Boolean);

        return (
          <Space direction="vertical" size={2}>
            {delegatesList.map((delName, idx) => (
              <Space key={idx} style={{ lineHeight: '1.3' }}>
                <UserOutlined style={{ color: '#096dd9', fontSize: '11px' }} />
                <Text strong style={{ color: '#096dd9', fontSize: '12px' }}>
                  {delName}
                </Text>
              </Space>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Médecin Visité',
      dataIndex: 'doctorName',
      key: 'doctorName',
      width: 220,
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {record.isPartner && <StarFilled style={{ color: '#fadb14', marginRight: 5 }} />}
            <MedicineBoxOutlined style={{ marginRight: 5, color: '#52c41a' }} />
            {name}
          </div>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.specialty} — {record.region}
          </Text>
        </div>
      )
    },
    {
      title: 'Type de Contrôle',
      dataIndex: 'controlType',
      key: 'controlType',
      width: 160,
      render: (type, record) => {
        let color = 'blue';
        const isCreation = type?.toUpperCase().includes('CREATION') || type?.toUpperCase().includes('CRÉATION');
        if (type?.toUpperCase().includes('INOPINÉ') || type?.toUpperCase().includes('CONTROLE')) color = 'volcano';
        if (type?.toUpperCase().includes('AUDIT')) color = 'magenta';
        if (isCreation) color = 'green';

        return (
          <Space direction="vertical" size={2}>
            <Tag color={color} style={{ fontWeight: 'bold' }}>
              {isCreation ? '✨ Création' : (type || 'Accompagnement')}
            </Tag>
            {record.doctorPotential && (
              <Tag color="cyan" style={{ fontSize: '10px', margin: 0, fontWeight: 'bold' }}>
                Méd: {record.doctorPotential} | Prod: {record.productPotential || '-'}
              </Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Observations Superviseur',
      dataIndex: 'comment',
      key: 'comment',
      width: 240,
      render: (comment, record) => {
        if (!comment) return <Text type="secondary">-</Text>;
        const maxLength = 40;
        const isLong = comment.length > maxLength;

        return (
          <div style={{ lineHeight: '1.4', fontSize: '12px' }}>
            <span>{isLong ? `${comment.substring(0, maxLength)}... ` : comment}</span>
            {isLong && (
              <Button 
                type="link" 
                size="small" 
                style={{ padding: 0, fontWeight: 'bold', marginLeft: 4 }}
                onClick={() => handleOpenCommentModal(record)}
              >
                Lire plus
              </Button>
            )}
          </div>
        );
      }
    },
    {
      title: 'Détails',
      key: 'actions',
      width: 90,
      fixed: 'right', // <-- Reste accessible même en scrollant
      render: (record) => (
        <Button 
          type="primary" 
          ghost 
          size="small"
          icon={<EyeOutlined />} 
          onClick={() => {
            Modal.info({
              title: `Rapport de Contrôle : ${record.doctorName}`,
              width: 650,
              content: (
                <div style={{ marginTop: 15, lineHeight: 1.8 }}>
                  <p><strong>👑 Superviseur :</strong> <Tag color="purple">{record.supervisorName}</Tag></p>
                  <p><strong>👥 Délégué(s) accompagné(s) :</strong> <Tag color="blue">{record.delegateName}</Tag></p>
                  <p><strong>🏥 Médecin :</strong> {record.doctorName} ({record.specialty} - {record.region})</p>
                  <p><strong>📅 Date :</strong> {dayjs(record.date).format('DD/MM/YYYY à HH:mm')}</p>
                  <p><strong>🎯 Type de visite :</strong> <Tag color="green" style={{ fontWeight: 'bold' }}>{record.controlType}</Tag></p>

                  {(record.doctorPotential || record.productPotential) && (
                    <div style={{ 
                      background: '#f6ffed', 
                      border: '1px solid #b7eb8f', 
                      borderRadius: 8, 
                      padding: '12px 16px', 
                      marginBottom: 12 
                    }}>
                      <div style={{ fontWeight: 'bold', color: '#389e0d', marginBottom: 6 }}>
                        🎯 Qualification de la Création :
                      </div>
                      <div style={{ display: 'flex', gap: 20 }}>
                        <span><strong>Potentiel Médecin :</strong> <Tag color="green">{record.doctorPotential}</Tag></span>
                        <span><strong>Potentiel Produit :</strong> <Tag color="cyan">{record.productPotential || '-'}</Tag></span>
                      </div>
                    </div>
                  )}

                  <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #eee' }} />
                  <p><strong>📝 Observations & Plan d'action du Superviseur :</strong></p>
                  <div style={{ background: '#f5f5f5', padding: 14, borderRadius: 6, whiteSpace: 'pre-wrap' }}>
                    {record.comment || record.fullFeedback}
                  </div>
                </div>
              ),
              okText: 'Fermer'
            });
          }}
        >
          Voir
        </Button>
      )
    }
  ];

  return (
    <div className="supervisor-page-container" style={{ padding: '16px', minHeight: '100vh', background: '#f0f2f5' }}>
      
      <style>{`
        @media (max-width: 768px) {
          .supervisor-page-container {
            padding: 10px !important;
          }
          .ant-card-head {
            flex-direction: column !important;
            align-items: flex-start !important;
            padding: 12px !important;
            gap: 10px !important;
          }
          .ant-card-extra {
            width: 100% !important;
            margin: 0 !important;
          }
        }
      `}</style>

      <Card 
        bordered={false}
        style={{ borderRadius: 10 }}
        bodyStyle={{ padding: '16px' }}
        title={
          <Space wrap>
            <SafetyCertificateOutlined style={{ color: '#722ed1', fontSize: '20px' }} />
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
              Rapports des Visites Superviseurs
            </span>
          </Space>
        }
        extra={
          <Space wrap>
            <Button size="small" icon={<DownloadOutlined />} onClick={handleExportCSV}>
              Exporter CSV
            </Button>
            <Button size="small" icon={<ReloadOutlined />} onClick={fetchReports} loading={loading}>
              Actualiser
            </Button>
          </Space>
        }
      >
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={10} lg={8}>
            <Input 
              prefix={<SearchOutlined />} 
              placeholder="Rechercher par superviseur, délégué, médecin..." 
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              size="middle"
            />
          </Col>
        </Row>

        {/* --- SCROLL HORIZONTAL FLUIDE SUR MOBILE ET TABLETTE --- */}
        <Table 
          columns={columns} 
          dataSource={filteredReports} 
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100 }} // <-- CLÉ DU RESPONSIVE : Active le défilement horizontal fluide
          size="middle"
          pagination={{ 
            pageSize: 10, 
            showTotal: (total) => `${total} rapports au total`,
            showSizeChanger: false
          }}
        />
      </Card>
    </div>
  );
};

export default SupervisorReportsPage;